import { useSession } from 'next-auth/react';
import { useQueryState } from 'nuqs';
import { useRecoilValue } from 'recoil';

import { Heading } from '@/components/heading';
import { JourneyHeader } from '@/components/modules/journey/header';
import { OptionList } from '@/components/modules/option/list';
import { OptionSummary } from '@/components/modules/option/summary';
import { Text } from '@/components/text';
import { DEFAULT_CARGO, DEFAULT_NUQS_CONFIG, MODE_ROAD, USER_ACCESS_FEATURES } from '@/lib/constants';
import { RouteModel } from '@/lib/models/route/types';
import { featuresState } from '@/state/features';
import { canAccessFeature } from '@/utils/auth';
import { QUERY_PARAM } from '@/utils/helpers/api-urls/constants';
import { parseAsCargoOption } from '@/utils/nuqs/parse-as-cargo-option';
import { tv } from '@/utils/styles';

export interface RouteDetailProps {
  route: RouteModel;
}

const RouteDetail = ({ route }: RouteDetailProps) => {
  const { data: session } = useSession();
  const canAccessRates = canAccessFeature(USER_ACCESS_FEATURES.RATES, session);
  const features = useRecoilValue(featuresState);

  const [cargo] = useQueryState(
    QUERY_PARAM.CARGO,
    parseAsCargoOption.withOptions(DEFAULT_NUQS_CONFIG).withDefault(DEFAULT_CARGO),
  );

  const { primaryMode, mainOptions, mainOptionEmissions, mainOptionSchedules, start, end } = route;
  const shouldShowRates = features.showMarketRates && canAccessRates;
  const showSimpleLayout = primaryMode === MODE_ROAD;
  const secondaryEmissions = (start?.emissions?.average || 0) + (end?.emissions?.average || 0);

  const { base, header, mobileMessage, mobileMessageBlock, hideOnMobile } = styles();

  return (
    <>
      <div className={base()}>
        <div className={header()}>
          <JourneyHeader
            originId={route.fromId}
            destinationId={route.toId}
            mode={primaryMode}
            shouldShowSchedulesLink
          />
        </div>
        <div className={mobileMessage()}>
          <div className={mobileMessageBlock()}>
            <Heading variant="h4" className="mb-base mt-0">
              Unfortunately this content is not yet optimised for mobile devices.
            </Heading>
            <Text variant="secondary">
              Please visit this site using a tablet or laptop to see more detailed information about this route.
            </Text>
          </div>
        </div>
      </div>
      <div className={hideOnMobile()}>
        {mainOptionSchedules && (
          <OptionSummary
            originId={route.fromId}
            destinationId={route.toId}
            mode={primaryMode}
            schedule={mainOptionSchedules}
            emissions={mainOptionEmissions}
            secondaryEmissions={secondaryEmissions}
            shouldShowRates={shouldShowRates}
            cargo={cargo}
          />
        )}
        <OptionList
          options={mainOptions}
          mode={primaryMode}
          emissionsStats={showSimpleLayout ? undefined : mainOptionEmissions}
          shouldShowRates={false}
        />
      </div>
    </>
  );
};

const styles = tv({
  slots: {
    base: 'bg-white',
    header: 'p-4 md:px-8 md:py-6',
    hideOnMobile: 'hidden md:block',
    mobileMessageBlock: 'rounded-lg bg-states-warning-backgroundColor p-4',
    messageBar:
      'items-center border border-x-0 border-y border-states-warning-borderColor bg-states-warning-backgroundColor px-8 py-2 text-baseSm',
    mobileMessage:
      "mt-6 block min-h-[50vh] bg-[url('/assets/blurred-details.png')] bg-contain bg-center bg-top px-2 py-6 md:hidden",
  },
});

export { RouteDetail };
